import { AlgoliaProductHit } from '@/scripts/libs/algolia/types/plp'
import wcs from '@/scripts/libs/wcs'
import algoliarecommend, { RecommendClient, RecommendModel } from '@algolia/recommend'

import makeDebug from 'debug'
const debug = makeDebug('Algolia:Recommend')

class AlgoliaRecommend {
  private readonly client: RecommendClient
  private readonly groupedIndex: string
  private readonly ungroupedIndex: string

  constructor() {
    const {
      algolia: { apiKey, applicationId, groupedIndex, ungroupedIndex },
    } = wcs.getStoreConfig()

    this.client = algoliarecommend(applicationId, apiKey)
    this.groupedIndex = groupedIndex
    this.ungroupedIndex = ungroupedIndex
  }

  async getFrequentlyBoughtTogether(objectID: string) {
    const frequentlyBoughtTogether =
      await this.client.getFrequentlyBoughtTogether<AlgoliaProductHit>([
        {
          indexName: this.groupedIndex,
          objectID,
        },
      ])
    debug('Frequently Bought Together: ', frequentlyBoughtTogether)
    return frequentlyBoughtTogether
  }

  async getRelatedProducts(objectID: string, context = '') {
    const relatedProducts = await this.client.getRelatedProducts<AlgoliaProductHit>([
      {
        indexName: this.groupedIndex,
        objectID,
        queryParameters: {
          ruleContexts: [context],
        },
      },
    ])
    debug('Related Products:', relatedProducts)
    return relatedProducts
  }

  async getTrendingItems({
    facetName,
    facetValue,
    hitsPerPage,
    threshold = 50,
  }: {
    facetName?: string
    facetValue?: string
    hitsPerPage: number
    threshold?: number
  }) {
    const trendingItems = await this.client.getTrendingItems<AlgoliaProductHit>([
      {
        indexName: this.groupedIndex,
        threshold,
        facetName,
        facetValue,
        maxRecommendations: hitsPerPage,
        queryParameters: {
          numericFilters: [`inventoryQuantity > 0`],
        },
      },
    ])
    debug('Trending Items:', trendingItems)
    return trendingItems
  }

  async getTrendingFacets(facetName: string, threshold = 80) {
    const trendingFacets = await this.client.getTrendingFacets<{
      facetName: string
      facetValue: string
      threshold: number
    }>([
      {
        indexName: this.groupedIndex,
        threshold,
        facetName,
      },
    ])
    const { categoriesToExcludeForTrendingFacets } = wcs.getStoreConfig()

    const filteredHits = trendingFacets.results[0].hits.filter(
      (hit) => !categoriesToExcludeForTrendingFacets.includes(hit.facetValue)
    )

    trendingFacets.results[0].hits = filteredHits
    trendingFacets.results[0].nbHits = filteredHits.length

    debug('Trending Facets:', trendingFacets)
    return trendingFacets
  }

  async getRecommendations(objectID: string, model: RecommendModel) {
    const recommandations = await this.client.getRecommendations<AlgoliaProductHit>([
      {
        indexName: this.groupedIndex,
        objectID,
        model,
      },
    ])
    debug('Recommandations:', recommandations)
    return recommandations
  }
}

export default new AlgoliaRecommend()
